import React from 'react';

interface HeroProps {
  onOpenSplash?: () => void;
}

// Editorial "panorama" hero (travel-journal style): masthead with the section
// pills, a bold one-line headline, the lede and route button, then the Kerry
// Park skyline as a wide rounded panorama with a rating stamp pinned to it. A strip of house facts runs underneath.

const RATING = '4.99';
const REVIEW_COUNT = '100+';

export const MASTHEAD_LINKS = [
  { href: '#stay-guide', label: 'The Stay' },
  { href: '#inside-the-house', label: 'The House' },
  { href: '#dining', label: 'Eat' },
  { href: '#neighborhoods-explore', label: 'Explore' },
  { href: '#climate', label: 'Seasons' },
] as const;

// Facts strip, in the order it reads; italic entries are the little asides.
export const HERO_FACTS: { text: string; aside?: boolean }[] = [
  { text: 'Walk Score 96' },
  { text: '431 Mbps Wi-Fi' },
  { text: 'Sleeps six' },
  { text: 'made for slow mornings', aside: true },
  { text: 'Rooftop grill' },
  { text: 'Built Green ★★★★' },
  { text: 'Own parking spot' },
];

const FactsRun: React.FC<{ hidden?: boolean }> = ({ hidden }) => (
  <ul className="ed-ticker-run" aria-hidden={hidden || undefined}>
    {HERO_FACTS.map((f) => (
      <li key={f.text} className={f.aside ? 'is-aside' : undefined}>
        {f.text}
        <span className="ed-ticker-star" aria-hidden="true">✶</span>
      </li>
    ))}
  </ul>
);

const Hero: React.FC<HeroProps> = () => (
  <section id="hero" className="hero ed-hero">
    <header className="ed-masthead">
      <a className="ed-logo" href="#hero" aria-label="Corazón — back to top">
        Corazón<i aria-hidden="true">.</i>
      </a>
      <nav className="ed-masthead-nav" aria-label="Guide sections">
        {MASTHEAD_LINKS.map((l, i) => (
          <a key={l.href} href={l.href} className={i === 0 ? 'is-lead' : undefined}>
            {l.label}
          </a>
        ))}
      </nav>
    </header>

    <div className="ed-hero-copy">
      <p className="hero-eyebrow ed-tag">Seattle · Capitol Hill</p>
      <h1 className="hero-title ed-hero-title">
        Hello <em>from the</em> Hill
      </h1>
      <div className="ed-hero-row">
        <p className="hero-subtitle">
          Everything you need for an easy Seattle stay—before arrival, while you&apos;re here, and when it&apos;s time to
          leave. Four floors, a rooftop, and Capitol Hill at your door.
        </p>
        <a className="ed-btn" href="#stay-guide">
          Start your stay route <span aria-hidden="true">→</span>
        </a>
      </div>
    </div>

    <figure className="ed-pano">
      <div className="ed-pano-frame">
        <img
          src="images/kerry-sunrise.webp"
          alt="Seattle's skyline and Mt. Rainier at sunrise, seen from Kerry Park"
          width={1600}
          height={900}
          fetchPriority="high"
        />
        <figcaption>Kerry Park at sunrise · the classic view of the city</figcaption>
      </div>
      <span className="ed-stamp">
        <b>{RATING}</b>
        <small>★ {REVIEW_COUNT} reviews</small>
      </span>
    </figure>

    <div className="ed-ticker" role="region" aria-label="The house at a glance">
      <div className="ed-ticker-track">
        <FactsRun />
        <FactsRun hidden />
      </div>
    </div>
  </section>
);

export default Hero;
